import type { TFunction } from 'i18next';
import { Globe, Send } from 'lucide-react';
import { useEffect, useId, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';
import { isWorkspaceEmail } from '@/auth/workspace-domain';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { apiClient } from '@/lib/api-client';
import { usePendingWrite } from '@/lib/use-pending-write';
import { problemMessage } from '../api/problem';
import { RoleChecklist } from '../roles/RoleChecklist';
import { useAssignableRoles } from '../roles/use-assignable-roles';
import { SidePanel } from '../ui/SidePanel';

const emailSchema = z.string().trim().email();

function useInviteForm({
  open,
  onOpenChange,
  placeKind,
  placeId,
  onInvited,
}: {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly placeKind: 'tenants' | 'organizations';
  readonly placeId: string;
  readonly onInvited: () => void;
}) {
  const { t } = useTranslation('administration');
  const { pending, run } = usePendingWrite();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [errors, setErrors] = useState<InviteErrors>({});

  const trimmed = email.trim();
  const external = emailSchema.safeParse(trimmed).success && !isWorkspaceEmail(trimmed);

  // Whatever closes the panel — «Annulla», Escape, its ×, a sent invitation — the next opening
  // starts from an empty form.
  useEffect(() => {
    if (!open) {
      setEmail('');
      setName('');
      setSelected(new Set());
      setErrors({});
    }
  }, [open]);

  const toggle = (roleId: string) =>
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(roleId)) {
        next.delete(roleId);
      } else {
        next.add(roleId);
      }
      return next;
    });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const found = validateInvite(t, trimmed, selected);
    setErrors(found);
    if (Object.keys(found).length > 0) {
      return;
    }
    void run(async () => {
      const { error, response } = await apiClient.POST(
        '/v1/administration/{placeKind}/{placeId}/invitations',
        {
          params: { path: { placeKind, placeId } },
          body: {
            email: trimmed,
            ...(name.trim() !== '' ? { displayName: name.trim() } : {}),
            roleIds: [...selected],
          },
        },
      );
      if (!response.ok) {
        setErrors({ form: problemMessage(t, error, response.status) });
        return;
      }
      toast.success(t('invite.done', { email: trimmed }));
      onOpenChange(false);
      onInvited();
    });
  };

  return { pending, email, setEmail, name, setName, selected, toggle, errors, external, submit };
}

/**
 * Invita (FR-IN-1): an email, an optional name, and one or more of the roles this caller may assign
 * at this place. No UID, no scope: the place is the one in the address, and the server checks the
 * right to grant each role before it writes anything (ADR-019).
 *
 * The console sends no message (decision 59), so the panel says so: the inviter tells the person,
 * who receives the roles at the first verified sign-in with that address. An address outside the
 * company is invited the same way and signs in by email link (FR-IN-6).
 */
type InviteErrors = { email?: string; roles?: string; form?: string };
type Translate = TFunction<'administration'>;

/** What is wrong with the form as it stands, by field; empty when it can be sent. */
function validateInvite(t: Translate, trimmed: string, selected: ReadonlySet<string>) {
  const found: InviteErrors = {};
  if (trimmed === '') {
    found.email = t('invite.emailRequired');
  } else if (!emailSchema.safeParse(trimmed).success) {
    found.email = t('invite.emailInvalid');
  }
  if (selected.size === 0) {
    found.roles = t('invite.rolesRequired');
  }
  return found;
}

function InviteEmailField({
  email,
  onChange,
  error,
  external,
}: {
  readonly email: string;
  readonly onChange: (email: string) => void;
  readonly error: string | undefined;
  readonly external: boolean;
}) {
  const { t } = useTranslation('administration');
  const emailId = useId();
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={emailId} className="field-label">
        {t('invite.email')}
      </label>
      <Input
        id={emailId}
        type="email"
        inputMode="email"
        autoComplete="off"
        value={email}
        onChange={(event) => onChange(event.target.value)}
        placeholder={t('invite.emailPlaceholder')}
        aria-invalid={error !== undefined}
        aria-describedby={error !== undefined ? `${emailId}-error` : undefined}
      />
      {error !== undefined ? (
        <p id={`${emailId}-error`} className="text-destructive text-[13px]">
          {error}
        </p>
      ) : null}
      {external ? (
        <p className="invite-note">
          <Globe aria-hidden="true" className="size-4 shrink-0" />
          {t('invite.external')}
        </p>
      ) : null}
    </div>
  );
}

function InviteNameField({
  name,
  onChange,
}: {
  readonly name: string;
  readonly onChange: (name: string) => void;
}) {
  const { t } = useTranslation('administration');
  const nameId = useId();
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={nameId} className="field-label">
        {t('invite.name')}{' '}
        <span className="text-muted-foreground font-normal">· {t('invite.optional')}</span>
      </label>
      <Input
        id={nameId}
        value={name}
        onChange={(event) => onChange(event.target.value)}
        placeholder={t('invite.namePlaceholder')}
      />
    </div>
  );
}

/** The roles the caller may assign: loading, failed, none, or the checklist. */
function InviteRolesChoice({
  roles,
  retry,
  selected,
  onToggle,
  placeName,
}: {
  readonly roles: ReturnType<typeof useAssignableRoles>['state'];
  readonly retry: () => void;
  readonly selected: ReadonlySet<string>;
  readonly onToggle: (roleId: string) => void;
  readonly placeName: string;
}) {
  const { t } = useTranslation('administration');
  if (roles.status === 'loading') {
    return (
      <p role="status" className="text-muted-foreground text-sm">
        {t('common.loading')}
      </p>
    );
  }
  if (roles.status === 'error') {
    return (
      <p role="alert" className="flex items-center gap-3 text-sm">
        {t('invite.rolesError')}
        <Button variant="outline" size="sm" onClick={retry}>
          {t('common.retry')}
        </Button>
      </p>
    );
  }
  if (roles.roles.length === 0) {
    return <p className="text-muted-foreground text-sm">{t('invite.noRoles')}</p>;
  }
  return (
    <RoleChecklist
      roles={roles.roles}
      selected={selected}
      onToggle={onToggle}
      legend={t('invite.roles', { place: placeName })}
    />
  );
}

function InviteRolesField({
  roles,
  retry,
  selected,
  onToggle,
  placeName,
  error,
}: {
  readonly roles: ReturnType<typeof useAssignableRoles>['state'];
  readonly retry: () => void;
  readonly selected: ReadonlySet<string>;
  readonly onToggle: (roleId: string) => void;
  readonly placeName: string;
  readonly error: string | undefined;
}) {
  const { t } = useTranslation('administration');
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-baseline justify-between gap-3">
        <span className="field-label">{t('invite.roles', { place: placeName })}</span>
        <span className="text-muted-foreground text-[12.5px]">
          {t('invite.chosen', { count: selected.size })}
        </span>
      </div>
      {error !== undefined ? (
        <p role="alert" className="text-destructive text-[13px]">
          {error}
        </p>
      ) : null}
      <InviteRolesChoice
        roles={roles}
        retry={retry}
        selected={selected}
        onToggle={onToggle}
        placeName={placeName}
      />
    </div>
  );
}

export function InvitePanel({
  open,
  onOpenChange,
  placeKind,
  placeId,
  placeName,
  onInvited,
}: {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly placeKind: 'tenants' | 'organizations';
  readonly placeId: string;
  readonly placeName: string;
  readonly onInvited: () => void;
}) {
  const { t } = useTranslation('administration');
  const { state: roles, retry } = useAssignableRoles(placeKind, placeId);
  const form = useInviteForm({ open, onOpenChange, placeKind, placeId, onInvited });
  const { pending, email, setEmail, name, setName, selected, toggle, errors, external } = form;
  const formId = useId();

  return (
    <SidePanel
      open={open}
      onOpenChange={onOpenChange}
      title={t('invite.title', { place: placeName })}
      description={t('invite.summary')}
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t('invite.cancel')}
          </Button>
          <Button type="submit" form={formId} disabled={pending}>
            <Send />
            {t('invite.submit')}
          </Button>
        </>
      }
    >
      <form id={formId} noValidate onSubmit={form.submit} className="flex flex-col gap-5">
        <InviteEmailField
          email={email}
          onChange={setEmail}
          error={errors.email}
          external={external}
        />
        <InviteNameField name={name} onChange={setName} />
        <InviteRolesField
          roles={roles}
          retry={retry}
          selected={selected}
          onToggle={toggle}
          placeName={placeName}
          error={errors.roles}
        />
        <p className="invite-note">{t('invite.notice')}</p>
        {errors.form !== undefined ? (
          <p role="alert" className="text-destructive text-sm">
            {errors.form}
          </p>
        ) : null}
      </form>
    </SidePanel>
  );
}
